import { useEffect, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import { useArchiveCompany } from './hooks/usePlatformAdminQueries';

interface ArchiveCompanyDialogProps {
  tenantId: string | null;
  tenantName: string;
  onOpenChange: (open: boolean) => void;
}

/**
 * «Архивировать компанию» — с вводом названия (AK-572).
 *
 * Архивация выкидывает из системы всех сотрудников компании разом: членства
 * отдаются только по активным компаниям, а живые сессии отзываются сразу.
 * Такое не должно случаться промахом мыши по соседнему пункту меню, поэтому
 * подтверждение — набранное название, а не «вы уверены?». Сервер проверяет
 * его тоже: диалог можно обойти, ручку — нет.
 *
 * Данные при этом целы: ни строки, ни схема тенанта не трогаются, возврат
 * из архива возвращает всё как было.
 */
export function ArchiveCompanyDialog({
  tenantId,
  tenantName,
  onOpenChange,
}: ArchiveCompanyDialogProps) {
  const [confirmName, setConfirmName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const archive = useArchiveCompany();
  const open = tenantId !== null;

  useEffect(() => {
    if (open) {
      setConfirmName('');
      setError(null);
    }
  }, [open, tenantId]);

  const matches = confirmName.trim() === tenantName.trim();

  const submit = async () => {
    if (!tenantId || !matches) return;
    setError(null);
    try {
      await archive.mutateAsync({ tenantId, confirmName: confirmName.trim() });
      onOpenChange(false);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.detail || 'Не удалось архивировать компанию'
          : 'Не удалось архивировать компанию',
      );
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={archive.isPending ? () => {} : onOpenChange}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Архивировать «{tenantName}»?</DialogTitle>
          <DialogDescription>
            Сотрудники компании потеряют доступ прямо сейчас — открытые вкладки
            закроются. Данные остаются на месте: компанию можно вернуть из
            архива, и всё будет как было.
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Label htmlFor="archive-confirm">
            Наберите название компании, чтобы подтвердить
          </Label>
          <Input
            id="archive-confirm"
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={tenantName}
            autoComplete="off"
          />

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={archive.isPending}
            >
              Отмена
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={!matches || archive.isPending}
            >
              {archive.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Архивировать
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ArchiveCompanyDialog;
